import { FontWeight } from '@graphql/generated-base';
import type { BrannanNavigation, Category, DesignLayoutType } from '@graphql/generated-base';
import type { OverlayableEventDesign, WebsiteV2DesignSite, WebsiteV2FontPackEntry } from './useEventWebsiteDesign.types';

type LegacyDesign = NonNullable<WebsiteV2DesignSite['legacyDesign']>;

const toLayoutType = (layoutType: string | null | undefined): DesignLayoutType | null =>
  layoutType === 'aloha' || layoutType === 'brannan' ? (layoutType as DesignLayoutType) : null;

const toBrannanNavigation = (navigation: string | null | undefined): BrannanNavigation | null =>
  navigation === 'classic' || navigation === 'singlePage' ? (navigation as BrannanNavigation) : null;

const toFontPackEntry = ({ category, fontFamily, sizePercent }: LegacyDesign['fonts'][number]): WebsiteV2FontPackEntry => ({
  __typename: 'FontPackConfiguration',
  id: `websiteV2:${category}`,
  category: category as Category,
  sizePercent,
  font: { __typename: 'Font', id: `websiteV2:${category}:${fontFamily}`, fontFamily },
});

// What the overlay writes, checked field by field against the V1 fields it may replace.
type Overlay = { -readonly [K in Exclude<keyof OverlayableEventDesign, 'fonts'>]?: OverlayableEventDesign[K] } & {
  fonts?: ReadonlyArray<WebsiteV2FontPackEntry>;
};
type ColorField = 'linkAndButtonColor' | 'artFontColor' | 'artBackgroundColor' | 'pageBackgroundColor' | 'pageFontColor';

/**
 * `eventDesign` with a Website V2 site's design laid over it. A V2 value wins; a field the site has no
 * value for (a template V1 can't adapt) keeps V1's, as `useEventWebsiteDesign` does. No site, or a site
 * with no design, returns `eventDesign` itself.
 *
 * Not overlaid: the V1 theme's own record (color schemes, Brannan transparency), the header layout and
 * design preferences. The named colors win over the color schemes, so a complete V2 palette decides every color.
 */
export const overlayWebsiteV2Design = <T extends OverlayableEventDesign>(eventDesign: T, site: WebsiteV2DesignSite | null | undefined): T => {
  const legacy = site?.legacyDesign;
  if (!legacy) return eventDesign;

  const overlay: Overlay = {};
  // V2 has no V1 custom art; its theme art is the sprite theme alone, and none is V1's `'blank'`.
  if (eventDesign.theme) overlay.theme = { ...eventDesign.theme, themeId: legacy.themeId ?? 'blank' };
  if ('useCustomTheme' in eventDesign) overlay.useCustomTheme = false;

  const layoutType = toLayoutType(legacy.layoutType);
  if (layoutType && eventDesign.websiteLayout) overlay.websiteLayout = { ...eventDesign.websiteLayout, layoutType };

  const brannanNavigation = toBrannanNavigation(legacy.brannanNavigation);
  if (brannanNavigation && 'brannanNavigation' in eventDesign) overlay.brannanNavigation = brannanNavigation;

  if (legacy.colors) {
    const { linkAndButton, artFont, artBackground, pageBackground, pageFont } = legacy.colors;
    const colors: Record<ColorField, string | null> = {
      linkAndButtonColor: linkAndButton,
      artFontColor: artFont,
      artBackgroundColor: artBackground,
      pageBackgroundColor: pageBackground,
      pageFontColor: pageFont,
    };
    (Object.keys(colors) as ColorField[]).filter(field => !!colors[field] && field in eventDesign).forEach(field => (overlay[field] = colors[field]));
  }

  // V1's base font is the layouts' event title font; its text transform is V1's, which V2 doesn't carry.
  if (legacy.titleFont && eventDesign.font) {
    overlay.font = { ...eventDesign.font, fontFamily: legacy.titleFont, ...('textTransform' in eventDesign.font && { textTransform: FontWeight.none }) };
  }
  if (legacy.fonts.length && 'fonts' in eventDesign) overlay.fonts = legacy.fonts.map(toFontPackEntry);

  // The one unchecked step: a caller's own `fonts` type may name fields a V2 entry lacks (see `WebsiteV2FontPackEntry`).
  return { ...eventDesign, ...overlay } as T;
};
